import { createColumnHelper } from '@tanstack/react-table';
import {
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { COLUMN_DEFAULT_SIZE_LG } from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { ReportMetricsResponse } from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { defaultColumnHelperAccessor } from 'src/tables/ReportTableCommon/DefaultColumnHelperAccessor';
import { LinkCell } from 'src/tables/ReportTableCommon/LinkCell';
import { SHIMMERING_DIV_HEIGHT_REPORT } from 'src/tables/ReportTableCommon/ReportTableCommon.constants';
import { reportV2AccessorFn } from 'src/tables/ReportTableCommon/ReportTableCommon.utils';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { ReportColumnPersonalizationFlags } from 'src/utils/columns/columnUtils.types';
import {
  PurchaseReportTableGroupingColumnId,
  PurchaseReportTableValueColumnId,
} from 'src/utils/columns/purchases/purchasesReportColumnUtils.types';
import {
  PURCHASE_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID,
  PURCHASE_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID,
} from 'src/utils/constants/contentIdMaps';
import { getPurchaseOrderRelativeUrl } from 'src/utils/purchaseUtils';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { ReportValueType } from 'src/WebApiController';

const columnHelper = createColumnHelper<ReportMetricsResponse | null>();

export const PURCHASE_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG = {
  [PurchaseReportTableGroupingColumnId.PurchaseOrderId]: columnHelper.accessor(
    reportV2AccessorFn(PurchaseReportTableGroupingColumnId.PurchaseOrderId),
    {
      id: PurchaseReportTableGroupingColumnId.PurchaseOrderId,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              PURCHASE_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                PurchaseReportTableGroupingColumnId.PurchaseOrderId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: ({ getValue }) => {
        const poId = getValue<string | null>();

        if (!poId) {
          return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
        }

        const poIds = [Number(poId)];

        return (
          <LinkCell
            text={poIds.map((poId) => `#${poId}`)}
            url={poIds.map((poId) => getPurchaseOrderRelativeUrl(poId))}
            separator={<span>{', '}</span>}
            openInNewTab
            align="left"
            style={{ fontWeight: 600 }}
          />
        );
      },
    }
  ),
} as const;

export const purchaseReportTableColumnDef = (
  columnId: string,
  columnValue: string,
  valueType: ReportValueType | undefined,
  personalizationFlags: Partial<ReportColumnPersonalizationFlags> | undefined
) => {
  const specialConfig = Object.entries(
    PURCHASE_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG
  ).find(([key]) => key === columnId)?.[1];

  if (specialConfig) {
    return specialConfig;
  }

  const headerContentId =
    PURCHASE_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
      columnId as unknown as PurchaseReportTableGroupingColumnId
    ] ||
    PURCHASE_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID[
      columnId as unknown as PurchaseReportTableValueColumnId
    ];

  return defaultColumnHelperAccessor(
    columnHelper,
    columnId,
    columnValue,
    headerContentId,
    ReportTypesV2.Purchase,
    valueType,
    personalizationFlags
  );
};
